import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import OnboardingView from '../views/OnboardingView.vue';
import WizardView from '../views/WizardView.vue';
import LearningBreakView from '../views/LearningBreakView.vue';
import type { SeoMetadata } from '@/types/seo';
import { applySeoMetadata } from '@/composables/useSeo';
import { EXPERIMENT_REGISTRY } from '@/stores/coordination/experimentRegistry';
import { useExperimentStore } from '@/stores/coordination/useExperimentStore';

// Define route meta interface for type safety
interface RouteMeta extends Record<PropertyKey, unknown> {
	requiresNavigation?: boolean;
	seo?: Partial<SeoMetadata>;
}

// Define typed routes for 100% type safety
const routes: RouteRecordRaw[] = [
	{
		path: '/',
		name: 'home',
		component: HomeView,
		meta: {
			seo: {
				title: 'Lerntypentest - 90 Fragen für echte Klarheit',
				description: 'Entdecken Sie den Lerntyp Ihres Kindes mit unserem wissenschaftlich fundierten 90-Fragen-Test. Kostenlos, ohne Anmeldung, sofortige Ergebnisse.',
				keywords: 'Lerntypentest, Lerntyp Test, Kinder Lernen, Lerntypen, Vester Test, Lernstrategie',
				openGraph: {
					title: 'Lerntypentest - 90 Fragen für echte Klarheit',
					description: 'Entdecken Sie den Lerntyp Ihres Kindes mit unserem wissenschaftlich fundierten 90-Fragen-Test.',
					type: 'website',
				},
			},
		} as RouteMeta,
	},
	{
		path: '/onboarding',
		name: 'onboarding',
		component: OnboardingView,
		meta: {
			seo: {
				title: 'Onboarding - Lerntypentest',
				description: 'Starten Sie den Lerntypentest für Ihr Kind. Nur wenige Angaben und schon kann es losgehen.',
				robots: 'noindex,follow',
			},
		} as RouteMeta,
	},
	{
		path: '/wizard/phase1/:step(\\d+)?',
		name: 'wizard-phase1',
		component: WizardView,
		props: (route) => ({
			phase: 1 as const,
			step: route.params.step ? (route.params.step as string) : undefined,
		}),
		meta: {
			seo: {
				title: 'Lerntypentest',
				description: 'Starten Sie den Lerntypentest für Ihr Kind. Nur wenige Angaben und schon kann es losgehen.',
				robots: 'noindex,follow',
			},
		} as RouteMeta,
	},
	{
		path: '/wizard/break',
		name: 'learning-break',
		component: LearningBreakView,
		meta: {
			seo: {
				title: 'Lerntypentest',
				description: 'Starten Sie den Lerntypentest für Ihr Kind. Nur wenige Angaben und schon kann es losgehen.',
				robots: 'noindex,follow',
			},
		 } as RouteMeta,
	},
	{
		path: '/wizard/phase2/:step(\\d+)?',
		name: 'wizard-phase2',
		component: WizardView,
		props: (route) => ({
			phase: 2 as const,
			step: route.params.step ? (route.params.step as string) : undefined,
		}),
		meta: {
			seo: {
				title: 'Lerntypentest',
				description: 'Starten Sie den Lerntypentest für Ihr Kind. Nur wenige Angaben und schon kann es losgehen.',
				robots: 'noindex,follow',
			},
		} as RouteMeta,
	},
	{
		path: '/results/basic',
		name: 'results-basic',
		component: () => import('../components/ResultsView.vue'),
		props: { basic: true },
		meta: {
			requiresNavigation: true,
			seo: {
				title: 'Lerntypentest',
				description: 'Starten Sie den Lerntypentest für Ihr Kind. Nur wenige Angaben und schon kann es losgehen.',
				robots: 'noindex,follow',
			},
		 } as RouteMeta,
	},
	{
		path: '/results/:id',
		name: 'results',
		component: () => import('../components/ResultsView.vue'),
		meta: { requiresNavigation: true } as RouteMeta,
	},
	{
		path: '/payment-success',
		name: 'payment-success',
		component: () => import('../views/PaymentSuccessView.vue'),
		meta: {
			seo: {
				robots: 'noindex,nofollow',
			},
		} as RouteMeta,
	},
	{
		path: '/thank-you',
		name: 'thank-you',
		component: () => import('../views/ThankYouView.vue'),
	},
	// Landing Page Variants - Typed for better maintainability
	{
		path: '/landing/struggling-parents',
		name: 'struggling-parents-landing',
		component: () => import('../views/landing/StrugglingParentsLanding.vue'),
	},
	{
		path: '/landing/ambitious-parents',
		name: 'ambitious-parents-landing',
		component: () => import('../views/landing/AmbitiousParentsLanding.vue'),
	},
	{
		path: '/landing/busy-parents',
		name: 'busy-parents-landing',
		component: () => import('../views/landing/BusyParentsLanding.vue'),
	},
	{
		path: '/landing/teacher-recommended',
		name: 'teacher-recommended-landing',
		component: () => import('../views/landing/TeacherRecommendedLanding.vue'),
	},
	{
		path: '/landing/results-showcase',
		name: 'results-showcase-landing',
		component: () => import('../views/landing/ResultsShowcaseLanding.vue'),
	},
	{
		path: '/landing/academic',
		name: 'academic-landing',
		component: () => import('../views/landing/AcademicLanding.vue'),
	},
	{
		path: '/landing/ergebnis-check',
		name: 'consultation-booking-landing',
		component: () => import('../views/landing/ConsultationBookingLanding.vue'),
		meta: {
			seo: {
				title: 'Persönliche Beratung zu Ihrem Lernprofil-Gutachten',
				description:
					'Besprechen Sie die Ergebnisse des Lerntypen-Tests mit unserer Expertin Cristina Maria Bogdan. Diplom-Pädagogin & Lerntherapeutin.',
				keywords: 'Lernberatung, Lerntherapie, Ergebnis-Beratung, Lernprofil Beratung, Ingolstadt',
				openGraph: {
					title: 'Ihr Lernprofil-Gutachten persönlich besprechen',
					description:
						'Gemeinsam entwickeln wir den optimalen Förderplan für Ihr Kind. Expertin mit Spezialisierung auf Angst- und Stressmanagement.',
					type: 'website',
				},
			},
		} as RouteMeta,
	},
	// Landing Page Index
	{
		path: '/landing',
		name: 'landing-index',
		component: () => import('../views/landing/LandingPageIndex.vue'),
	},
	// Component Showcase
	{
		path: '/component-showcase',
		name: 'component-showcase',
		component: () => import('../views/ComponentShowcaseView.vue'),
	},
	// Test Pages (E2E Testing)
	{
		path: '/test/payment-modal',
		name: 'payment-modal-test',
		component: () => import('../views/test/PaymentModalTestPage.vue'),
		meta: {
			seo: {
				robots: 'noindex,nofollow',
			},
		} as RouteMeta,
	},
	// Info Pages (SEO/SEA Content Pages)
	{
		path: '/info/lerntypen',
		name: 'lerntypen-info',
		component: () => import('../views/info/LerntypenInfoPage.vue'),
		meta: {
			seo: {
				title: 'Lerntypen verstehen - Visuell, Auditiv, Kinästhetisch',
				description: 'Erfahren Sie alles über die verschiedenen Lerntypen und wie Sie Ihr Kind optimal fördern können. Wissenschaftlich fundierte Informationen zu visuellen, auditiven und kinästhetischen Lernern.',
				keywords: 'Lerntypen, visueller Lerner, auditiver Lerner, kinästhetischer Lerner, Lernstile',
				openGraph: {
					type: 'article',
				},
			},
		} as RouteMeta,
	},
	{
		path: '/info/hausaufgaben-tipps',
		name: 'hausaufgaben-tipps-info',
		component: () => import('../views/info/HausaufgabenTippsPage.vue'),
		meta: {
			seo: {
				title: 'Hausaufgaben Tipps - Effektive Strategien für Eltern',
				description: 'Praktische Tipps und Strategien für stressfreie Hausaufgaben. Erfahren Sie, wie Sie Ihr Kind optimal beim Lernen unterstützen können.',
				keywords: 'Hausaufgaben Tipps, Lernen zu Hause, Hausaufgaben Hilfe, Lernstrategien',
				openGraph: {
					type: 'article',
				},
			},
		} as RouteMeta,
	},
	{
		path: '/info/lernschwierigkeiten',
		name: 'lernschwierigkeiten-info',
		component: () => import('../views/info/LernschwierigkeitenInfoPage.vue'),
		meta: {
			seo: {
				title: 'Lernschwierigkeiten erkennen und überwinden',
				description: 'Lernschwierigkeiten frühzeitig erkennen und gezielt unterstützen. Informationen zu Ursachen, Symptomen und Lösungsansätzen.',
				keywords: 'Lernschwierigkeiten, Lernprobleme, Lernstörungen, Lernblockaden',
				openGraph: {
					type: 'article',
				},
			},
		} as RouteMeta,
	},
	// Legal Pages
	{
		path: '/impressum',
		name: 'impressum',
		component: () => import('../views/legal/ImpressumPage.vue'),
	},
	{
		path: '/datenschutz',
		name: 'datenschutz',
		component: () => import('../views/legal/DatenschutzPage.vue'),
	},
	{
		path: '/agb',
		name: 'agb',
		component: () => import('../views/legal/AGBPage.vue'),
	},
	// 404 Not Found - Must be last
	{
		path: '/:pathMatch(.*)*',
		name: 'not-found',
		component: () => import('../views/NotFoundView.vue'),
	},
	// Legacy route redirect for backward compatibility
	{
		path: '/wizard/:step(\\d+)?',
		redirect: (to) => {
			const step = to.params.step || '1';
			return `/wizard/phase1/${step}`;
		},
	},
] as const;

const router = createRouter({
	history: createWebHistory(),
	routes,
	// Scroll behavior: always scroll to top on route change with smooth behavior
	scrollBehavior(_to, _from, savedPosition) {
		// If there's a saved position (browser back/forward), use it
		if (savedPosition) {
			return {
				...savedPosition,
				behavior: 'smooth',
			};
		}

		// For all route changes, scroll to top smoothly
		// WizardContainer will also handle its own scrolling for better UX
		return {
			top: 0,
			behavior: 'smooth',
		};
	},
});

// Flag to ensure experiment activation runs only once on initial page load
let experimentActivationDone = false;

// Add type-safe navigation guards
router.beforeEach((to, _from, next) => {
	// ─── Experiment Activation (runs once on initial page load) ──────────
	if (!experimentActivationDone) {
		experimentActivationDone = true;

		const experimentParam = to.query.experiment;
		if (typeof experimentParam === 'string' && experimentParam.length > 0) {
			const experimentId = experimentParam;
			const registry = EXPERIMENT_REGISTRY as Record<string, (typeof EXPERIMENT_REGISTRY)[keyof typeof EXPERIMENT_REGISTRY]>;
			const experiment = registry[experimentId];

			if (experiment) {
				const experimentStore = useExperimentStore();
				const variantParam = to.query.variant;
				const explicitVariant = typeof variantParam === 'string' ? variantParam : undefined;

				experimentStore.activateExperiment(experiment, explicitVariant);

				// Strip experiment params from URL for clean browser bar
				const cleanQuery = { ...to.query };
				delete cleanQuery.experiment;
				delete cleanQuery.variant;
				next({ ...to, query: cleanQuery, replace: true });
				return;
			}
		}
	}

	// ─── Navigation meta ────────────────────────────────────────────────
	const requiresNavigation = to.meta?.requiresNavigation as boolean | undefined;

	if (requiresNavigation) {
		// Add navigation logic here if needed
		// For now, allow all navigation
	}

	// Apply SEO metadata from route meta on every navigation
	// This ensures meta tags update when navigating between routes
	const seoMeta = to.meta?.seo as Partial<SeoMetadata> | undefined;
	if (seoMeta) {
		applySeoMetadata(seoMeta);
	}

	next();
});

export default router;
